import { BaserowPlugin } from '@baserow/modules/core/plugins'
import { EnterprisePlugin } from '@baserow_enterprise/plugins'

import OpcionQuitarLogo from '@extras_mg/components/OpcionQuitarLogo'
import SelectorDePiel from '@extras_mg/components/SelectorDePiel'
import TituloDeTabla from '@extras_mg/components/TituloDeTabla'
import PestanasDeVistas from '@extras_mg/components/PestanasDeVistas'
import BotonNuevoElemento from '@extras_mg/components/BotonNuevoElemento'
import NuevoGrupo from '@extras_mg/components/NuevoGrupo'
import SelectorDeTema from '@extras_mg/components/SelectorDeTema'
import GestionarMiembrosItem from '@extras_mg/components/GestionarMiembrosItem'
import RegistroSidebarEspacio from '@extras_mg/components/RegistroSidebarEspacio'
import DependenciaFechasItem from '@extras_mg/components/DependenciaFechasItem'
import AccesoAdjuntosItem from '@extras_mg/components/AccesoAdjuntosItem'
import VigiaDeFechas from '@extras_mg/components/VigiaDeFechas'
import CandadoDeColumna from '@extras_mg/components/CandadoDeColumna'
import CapaDeLaApp from '@extras_mg/components/CapaDeLaApp'
import UsarPlantilla from '@extras_mg/components/UsarPlantilla'
import MenuSupervision from '@extras_mg/components/MenuSupervision'

// Un componente que no dibuja nada, para ocupar el sitio del panel de Baserow.
// No es un archivo .vue porque no hay plantilla que escribir: todo su trabajo es
// existir para que workspace.vue no pinte el suyo.
const SinAyudaDeBaserow = {
  name: 'SinAyudaDeBaserow',
  render: () => null,
}

/**
 * Reemplaza al plugin de premium en el registro, con SU MISMO nombre de tipo.
 *
 * Es la unica forma de poner nuestro interruptor de "quitar el logo" sin dejar
 * dos —el suyo con candado y el nuestro— en el panel de compartir. Conservar el
 * nombre 'premium' no es un capricho: PaidFeaturesModal hace
 * $registry.get('plugin', 'premium') directo, asi que si lo quitaramos sin mas,
 * ese modal reventaria al abrirse desde cualquier funcion que siga bloqueada.
 *
 * Que se pierde al reemplazarlo: impersonar usuarios, la insignia de licencia,
 * la exportacion desde vistas publicas y los botones de compra del modal.
 * Ninguna funcionaba sin licencia, asi que no se pierde nada que sirviera.
 * getPremiumModalButtonsComponent devuelve null y el modal lo tolera: lo pinta
 * con v-if.
 */
export class ExtrasMGPlugin extends BaserowPlugin {
  static getType() {
    return 'premium'
  }

  getAdditionalShareLinkOptions() {
    return [OpcionQuitarLogo]
  }

  /**
   * Interruptor de tema claro / oscuro en el menu de usuario.
   *
   * Baserow no tiene modo oscuro —el negro de esta instancia lo pinta nuestro
   * CSS—, asi que el interruptor tambien tiene que ser nuestro. Este hook es
   * del core y pinta cada componente como un <li> del menu.
   */
  /**
   * Quita el panel de "Necesitamos tu ayuda" del tablero.
   *
   * Es el que pide compartir Baserow en redes y darle una estrella en GitHub.
   * Tiene sentido en una instancia publica de ellos; en la de un cliente, no:
   * quien entra aqui viene a ver sus tablas, no a promocionar la herramienta.
   *
   * No se parchea nada del arbol de Baserow. workspace.vue pinta su panel SOLO
   * si ningun plugin ofrece uno propio (`v-if="dashboardHelpComponents.length
   * === 0"`), asi que basta con devolver un componente que no dibuja nada. Es
   * el mismo hueco por el que enterprise mete el suyo cuando hay licencia.
   */
  getDashboardHelpComponents() {
    return [SinAyudaDeBaserow]
  }

  getUserContextComponents() {
    // MenuSupervision solo se pinta para Victor e Isaac (ver supervision.js).
    return [MenuSupervision, SelectorDeTema, SelectorDePiel]
  }

  /**
   * Lo que va en todas las paginas con sesion: la supervision (el aviso de
   * "estas supervisando este espacio" y su modal de confirmacion, que a quien
   * no supervisa no le pintan nada) y el asistente de ayuda.
   */
  getAppLayoutComponent() {
    return CapaDeLaApp
  }

  hasFeature() {
    // Sin licencia de Baserow no hay ninguna funcion suya disponible, que es
    // exactamente lo que respondia el de premium.
    return false
  }

  getImpersonateComponent() {
    return null
  }

  getHighestLicenseTypeBadge() {
    return null
  }

  getAdditionalTableHeaderComponents(view, isPublic) {
    // La cabecera de un tablero de Monday: el nombre arriba, las vistas como
    // pestañas debajo y el botón azul de "Nuevo elemento" (con su flecha de
    // "Nuevo grupo") al principio de la barra. En una vista publica no va nada de esto: ahi el visitante no
    // navega tablas ni vistas, solo ve la que le compartieron.
    //
    // UsarPlantilla solo aparece en las tablas de la base de plantillas.
    //
    // VigiaDeFechas no pinta nada: trae las filas que la dependencia de fechas
    // marco como invalidas. Va aqui porque es lo unico que se monta una vez por
    // tabla abierta. En la publica no: el visitante no puede leer las reglas.
    return isPublic
      ? []
      : [TituloDeTabla, PestanasDeVistas, BotonNuevoElemento, NuevoGrupo, UsarPlantilla, VigiaDeFechas]
  }

  getPremiumModalButtonsComponent() {
    return null
  }
}

/**
 * Reemplaza al plugin de enterprise, con SU MISMO nombre de tipo, para quitarle
 * a la barra lateral los dos enlaces que no llevan a ningun lado sin licencia:
 * "Soporte directo" (es el soporte de la empresa Baserow, no el nuestro) y
 * "Registro de auditoria".
 *
 * Se HEREDA en vez de dar de baja el plugin entero porque enterprise cuelga de
 * ahi mas cosas —el logo, el modal de instantaneas, los iconos de tipo de
 * campo— y no todas estan bloqueadas. Pisando un solo metodo se quita lo que
 * estorba y lo demas sigue igual.
 *
 * El asistente se queda: es lo unico de esos tres que no es una puerta cerrada.
 */
export class EnterpriseSinCandadosPlugin extends EnterprisePlugin {
  static getType() {
    return 'enterprise'
  }

  /**
   * "Gestionar miembros" en el menú de una base: el de enterprise pedía
   * licencia (candado y modal de precios); el nuestro funciona y solo sale a
   * quien puede usarlo (admin del espacio o Encargado de la base).
   */
  getAdditionalApplicationContextComponents(workspace, application) {
    return [GestionarMiembrosItem]
  }

  /**
   * Lo mismo en el menú de cada tabla, más la dependencia de fechas y quién
   * abre los adjuntos (este, solo al admin del espacio).
   */
  getAdditionalApplicationChildContextComponents(workspace, application, item) {
    if (application.type !== 'database') {
      return []
    }
    return [GestionarMiembrosItem, DependenciaFechasItem, AccesoAdjuntosItem]
  }

  /**
   * En el menú de una vista enterprise solo ponía cosas con candado (roles
   * de vista restringida y dependencias de fecha). Fuera.
   */
  getAdditionalViewContextComponents(workspace, table, view) {
    return []
  }

  /**
   * Enterprise pinta un icono junto al título de las columnas que usa su
   * dependencia de fechas; sin licencia solo servía para abrir el candado. En
   * su lugar va el candado de los permisos por columna, que solo se pinta en
   * las columnas que tú no escribes.
   */
  getGridViewFieldTypeIconsBefore(workspace, view, field) {
    return [CandadoDeColumna]
  }

  getSidebarWorkspaceComponents(workspace) {
    return super
      .getSidebarWorkspaceComponents(workspace)
      .filter((componente) => {
        const nombre = componente?.name || componente?.__name || ''
        return !['ChatwootSupportSidebarWorkspace', 'AuditLogSidebarWorkspace'].includes(
          nombre
        )
      })
      .concat([RegistroSidebarEspacio])
  }
}
